import React, {useContext, useMemo} from 'react'
import {Bar} from 'react-chartjs-2'
import {CircularProgress} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {useSelector} from 'react-redux'
import {DashboardContext} from './DashboardContext'
import {ratingColors8, ratingListLabel9, ratingTexts8} from 'keys/rating'
import {RatingBarChartStyles} from './Style'
import {getCurrentSemesterAndYear} from "../../utils/general";

const reversedLabels = [...ratingListLabel9].reverse()
const reversedColors = [...ratingColors8].reverse()
const allBarLabels = [...reversedLabels]
const allBarColors = [...reversedColors, '#E2E8F0']

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {display: false},
        datalabels: {display: false},
        tooltip: {
            displayColors: false,
            callbacks: {
                label: (context) => {
                    const pct = context.dataset.percentages?.[context.dataIndex] ?? 0
                    return `${context.raw} תלמידים (${pct}%)`
                }
            }
        }
    },
    scales: {
        y: {
            ticks: {
                callback: (v) => `${v} תלמידים`,
                stepSize: 5
            },
            grid: {color: '#E2E8F0'}
        },
        x: {
            grid: {display: false},
            ticks: {font: {family: 'Calibri, sans-serif', size: 13}}
        }
    }
}

const coloredLabel = (item) => (
    <span style={{color: item.color, fontWeight: 700}}>{item.label}</span>
)

const ratingPrefix = (label) => label === 'בטווח המצופה' ? '' : 'בדירוג '

const QuickSummary = ({items, dataCount, notPerformedCount, redoCount, isCurrentSemesterAndYear,}) => {
    const {brokenDisplay} = useSelector(state => state.general)
    if (!items.length) return null
    const [first, second, ...rest] = items
    const showWarning = isCurrentSemesterAndYear && notPerformedCount > 0
    return (
        <div className='quick-summary'>
            <div className='summary-title'>{'סקירה מהירה של ביצועי '}<span className={`${brokenDisplay && 'blurred2'}`}>{dataCount}</span>{' תלמידים'}</div>
            <div className='summary-body'>
                <div className='summary-line'>
                    <span className={`${brokenDisplay && 'blurred2'}`}>{first.count}</span>{` תלמידים נמצאים ${ratingPrefix(first.label)}`}{coloredLabel(first)}{' - הקבוצה הגדולה ביותר.'}
                </div>
                {second && (
                    <div className='summary-line'>
                        {'אחריהם '}<span className={`${brokenDisplay && 'blurred2'}`}>{second.count}</span>{` תלמידים ${ratingPrefix(second.label)}`}{coloredLabel(second)}{rest.length > 0 ? ',' : '.'}
                    </div>
                )}
                {rest.length > 0 && (
                    <div className='summary-line'>
                        {rest.map((item, i) => (
                            <React.Fragment key={item.label}>
                                <span className={`${brokenDisplay && 'blurred2'}`}>{item.count}</span>{` ${ratingPrefix(item.label)}`}{coloredLabel(item)}
                                {i < rest.length - 2 ? ', ' : i === rest.length - 2 ? ' ו- ' : '.'}
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {showWarning && (
                    <div className='warning-line'>
                        <ErrorOutlineIcon className='warning-icon'/>
                        <span><strong className={`${brokenDisplay && 'blurred2'}`}>{notPerformedCount}</strong>{' תלמידים עוד לא ביצעו את סוללת המבדקים.'}
                            {redoCount > 0 && (
                                <>
                                    {" ול-"}<strong className={`${brokenDisplay && 'blurred2'}`}>{redoCount}</strong>{' יש מבדקים לביצוע חוזר.'}
                                </>
                            )}                            {' כדאי להשלים כדי לקבל תמונה מלאה.'}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

const RatingBarChart = ({ compact = false }) => {
    const {correctWordsPerMinuteDoughnut, dashboardData, assessmentFilters} = useContext(DashboardContext)
    const {data, isFetching} = correctWordsPerMinuteDoughnut;
    const assessmentFiltersSemester = assessmentFilters.semester_and_year?.semester?.id;
    const assessmentFiltersYear = assessmentFilters.semester_and_year?.school_year?.id;
    const currentSemester = getCurrentSemesterAndYear()?.semester?.id;
    const currentYear = getCurrentSemesterAndYear()?.school_year?.id;
    const isCurrentSemester = currentSemester === assessmentFiltersSemester;
    const isCurrentYear = currentYear === assessmentFiltersYear;
    const isCurrentSemesterAndYear = isCurrentYear && isCurrentSemester;

    const counts = useMemo(() => {
        const d = correctWordsPerMinuteDoughnut.data
        if (!d) return null
        return [5, 4, 3, 2, 1].map(i => d[i]?.FLUENCY?.count || 0)
    }, [correctWordsPerMinuteDoughnut.data])

    const percentages = useMemo(() => {
        const d = correctWordsPerMinuteDoughnut.data
        if (!d) return null
        return [5, 4, 3, 2, 1].map(i => d[i]?.FLUENCY?.percentage || 0)
    }, [correctWordsPerMinuteDoughnut.data])

    const dataCount = useMemo(() => {
        if (!counts) return 0;
        return counts.reduce((s, c) => s + c, 0)
    }, [counts, dashboardData])
    const chartData = useMemo(() => {
        if (!counts) return null
        return {
            labels: allBarLabels,
            datasets: [{
                data: counts,
                percentages,
                backgroundColor: allBarColors,
                borderWidth: 0,
                borderRadius: 10,
                borderSkipped: false,
                categoryPercentage: 1.0,
                barPercentage: 0.75
            }]
        }
    }, [counts, percentages])

    const summary = useMemo(() => {
        if (!data) return null
        // ratingListLabel9[0]="גבוה"=rating5, [4]="נמוך מאד"=rating1
        return ratingListLabel9
            .map((label, i) => ({label, count: data[i + 1]?.FLUENCY?.count || 0, color: ratingTexts8[i]}))
            .filter(item => item.count > 0)
            .sort((a, b) => b.count - a.count)
    }, [data])

    const hasAnyData = counts && counts.some(v => v > 0)

    if (isFetching) {
        return (
            <RatingBarChartStyles compact={compact}>
                <div className='no-results'>
                    <CircularProgress color="thirdly"/>
                </div>
            </RatingBarChartStyles>
        )
    }

    if (!chartData || !hasAnyData) {
        return (
            <RatingBarChartStyles compact={compact}>
                <div className='no-results'>{'אין עדיין תוצאות'}</div>
            </RatingBarChartStyles>
        )
    }

    return (
        <RatingBarChartStyles compact={compact}>
            <QuickSummary items={summary || []} dataCount={dataCount} notPerformedCount={data?.not_performed_count ?? 0}
                          redoCount={data?.redo_count ?? 0} isCurrentSemesterAndYear={isCurrentSemesterAndYear}/>
            <div className='bar-wrap'>
                <Bar data={chartData} options={chartOptions}/>
            </div>
        </RatingBarChartStyles>
    )
}

export default RatingBarChart
